import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import type { KeeperDuelWebViewRef, KeeperDuelStartConfig } from '../../../src/quiz/hooks/useKeeperDuelGame';

interface Props {
  html: string;
  onMessage: (event: { nativeEvent: { data: string } }) => void;
}

/**
 * Web host: the game runs in a same-origin `srcDoc` iframe. The game posts to `window.parent`; we forward
 * those as `{ nativeEvent: { data } }` so the hook's handler is platform-agnostic. Config is applied by
 * calling the iframe's exposed `__keeperDuelApplyStart` directly (srcDoc inherits the parent origin).
 */
export const KeeperDuelWebView = forwardRef<KeeperDuelWebViewRef, Props>(function KeeperDuelWebView({ html, onMessage }, ref) {
  const frameRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(ref, () => ({
    applyStart: (config: KeeperDuelStartConfig) => {
      const win = frameRef.current?.contentWindow as (Window & { __keeperDuelApplyStart?: (c: KeeperDuelStartConfig) => void }) | null;
      win?.__keeperDuelApplyStart?.(config);
    },
    reload: () => {
      // Re-running srcDoc reboots the runtime (re-posts `ready`).
      const el = frameRef.current;
      if (el) { const doc = el.srcdoc; el.srcdoc = ''; el.srcdoc = doc; }
    },
  }), []);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== frameRef.current?.contentWindow) return;
      if (typeof e.data !== 'string') return;
      onMessage({ nativeEvent: { data: e.data } });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onMessage]);

  return (
    <iframe
      ref={frameRef}
      title="Keeper Duel"
      srcDoc={html}
      style={{ flex: 1, width: '100%', height: '100%', border: 'none', background: 'transparent' }}
    />
  );
});
